import dayjs from 'dayjs';
import type { ConfigType, OpUnitType, QUnitType } from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import calendar from 'dayjs/plugin/calendar';
import duration from 'dayjs/plugin/duration';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isToday from 'dayjs/plugin/isToday';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(advancedFormat);
dayjs.extend(isSameOrAfter);
dayjs.extend(relativeTime);
dayjs.extend(duration);
dayjs.extend(isToday);
dayjs.extend(timezone);
dayjs.extend(calendar);

export const getLocalFromUTC = (time) => dayjs.utc(time).local();

export const dateUTCFormat = (
  timestamp: number | string,
  format = 'MMMM DD, YYYY [at] h:mma'
) => {
  return getLocalFromUTC(timestamp).format(format);
};
export const dateUnixFormat = (
  timestamp: number,
  format = 'MMMM DD, YYYY [at] h:mma'
) => {
  return dayjs.unix(timestamp).format(format);
};

export const dateUnixLocalFormat = (
  timestamp: number,
  format = 'MMM DD, YYYY [at] h:mma'
) => {
  return dayjs.unix(timestamp).local().format(format);
};

export const dateUTCFromNow = (
  timestamp: ConfigType,
  withoutSuffix?: boolean
): string => {
  return dayjs.utc(timestamp).fromNow(withoutSuffix);
};

export const dateUtcFromTime = (
  startTime: ConfigType,
  comparedTime: ConfigType,
  withoutSuffix?: boolean
) => {
  return dayjs.utc(startTime).from(comparedTime, withoutSuffix);
};

export const MMMDoYYYYhmma = (timestamp: number | string) =>
  dateUTCFormat(timestamp, 'MMM Do, YYYY [at] h:mma');

export const MMMDoYYYY = (timestamp: number | string) =>
  dateUTCFormat(timestamp, 'MMM Do, YYYY');

export const MMMDoYYYYUnix = (timestamp: number) =>
  dateUnixFormat(timestamp, 'MMM Do, YYYY');

export const getDaysFromNow = (timestamp: number) =>
  dayjs.unix(timestamp).diff(dayjs().endOf('date'), 'day') + 1;

export const getIsToday = (timestamp: number) =>
  dayjs.unix(timestamp).endOf('date').isToday();

export const getTimeStringFromNow = (timestamp: number) =>
  dayjs.unix(timestamp).fromNow(true);

export const isFuture = (timestamp: number) =>
  dayjs.unix(timestamp).isAfter(dayjs());

export const isBefore = (timestamp: number, timestamp2: number) =>
  dayjs.unix(timestamp).isBefore(dayjs.unix(timestamp2), 'd');

export const MMMMDoYYYY = (timestamp: number | string) => {
  return dateUTCFormat(timestamp, 'MMMM Do, YYYY');
};

export const sortByDate = (array, keyName): { keyName: string } => {
  return array.sort((a, b) => {
    const aTime = new Date(a[keyName]).getTime();
    const bTime = new Date(b[keyName]).getTime();
    return bTime > aTime ? 1 : -1;
  });
};

export const getUTCFulldate = (utc) => {
  const dayUTC = dayjs.utc(utc);
  return `${dayUTC.format('MMMM DD, YYYY')} at ${dayUTC.format('h:mma')}`;
};

export const getUTCFromNow = (utc) => dayjs.utc(utc).fromNow();
export const getUTCLocalHMMA = (utc) => dayjs.utc(utc).format('h:mma');
export const getMMMDYYYYathmmA = () =>
  dayjs().format('MMM D, YYYY [at] h:mm A');

export const secondsToHHmmss = (seconds) => {
  return seconds > 3600
    ? dayjs.duration(seconds * 1000).format('HH:mm:ss')
    : dayjs.duration(seconds * 1000).format('mm:ss');
};

export const formatSeconds = (
  seconds: number,
  options?: {
    format?: string;
    formatForLessThanHour?: string;
    formatForLessThanMinute?: string;
  }
) => {
  return seconds > 3600
    ? dayjs.duration(seconds, 'seconds').format(options.format || 'HH:mm:ss')
    : seconds > 60
      ? dayjs
          .duration(seconds, 'seconds')
          .format(options.formatForLessThanHour || 'mm:ss')
      : dayjs
          .duration(seconds, 'seconds')
          .format(options.formatForLessThanMinute || 'mm:ss');
};

export const addDaysUnixDate = (days: number) => dayjs().add(days, 'd').unix();

export const getDiff = (time: ConfigType, unit?: QUnitType | OpUnitType) =>
  dayjs().diff(time, unit);

export const getTimeFromNow = (milliseconds: number) =>
  dayjs().add(milliseconds, 'milliseconds').fromNow();

export const convertSecondsToFormat = (seconds: number, format: string) => {
  return dayjs.duration(seconds * 1000).format(format);
};

export const dateFormat = (time: string, format: string) => {
  return dayjs(time).format(format);
};

export const getNowInFormat = (format: string) => {
  return dayjs().format(format);
};

export const getNowInFormatUTC = (format: string) => {
  return dayjs().format(format);
};

export const getDateFromDaysAgo = (
  timePeriod: number,
  subtractedSeconds = 0,
  format = 'YYYY-MM-DDTHH:mm:ssZ'
) => {
  return dayjs()
    .utc()
    .subtract(timePeriod, 'day')
    .subtract(subtractedSeconds, 'second')
    .format(format);
};

export const getDateFromDaysAgoUTC = (
  timePeriod: number,
  subtractedSeconds = 0,
  format = 'YYYY-MM-DDTHH:mm:ssZ'
) => {
  return dayjs()
    .utc()
    .subtract(timePeriod, 'day')
    .subtract(subtractedSeconds, 'second')
    .format(format);
};

export const getNowTimestampSeconds = () => {
  return dayjs().unix();
};

export const getTimezoneName = () => {
  return dayjs.tz.guess();
};

export const getDateInTimestampMillisecond = (date: string) => {
  return dayjs(date).unix();
};

export const addTimeDisplayFormat = (
  date: string,
  number: number,
  format: string
) => {
  return dayjs(date).add(number, 'day').format(format);
};

export const parseTime = (time, divide, module) => {
  const res = Math.floor((time / divide) % module);
  return `0${res}`.slice(-2);
};

export const getCalendarDate = (date: ConfigType) => {
  return dayjs(date).calendar();
};
